import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import api from '../lib/axios';
import { MessageCircle, Send, Search, Phone, User, Check, CheckCheck, Clock, AlertCircle, Loader2, RefreshCw, Link2 } from 'lucide-react';
import { TitleWithHelp } from '../components/ui/help-tooltip';

export default function WhatsAppChat() {
  const [conversations, setConversations] = useState([]);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newChatPhone, setNewChatPhone] = useState('');
  const [showNewChat, setShowNewChat] = useState(false);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = useCallback(async () => {
    try {
      const res = await api.get('/whatsapp/conversations');
      setConversations(res.data);
    } catch (err) {
      console.error('Erro ao carregar conversas:', err);
    } finally {
      setLoadingConvs(false);
    }
  }, []);

  const loadMessages = useCallback(async (phone) => {
    setLoadingMsgs(true);
    try {
      const res = await api.get(`/whatsapp/messages/${phone}`);
      setMessages(res.data);
    } catch (err) {
      toast.error('Erro ao carregar mensagens');
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    loadConversations();
    pollRef.current = setInterval(loadConversations, 10000);
    return () => clearInterval(pollRef.current);
  }, [loadConversations]);

  useEffect(() => {
    if (selectedPhone) {
      loadMessages(selectedPhone);
    }
  }, [selectedPhone, loadMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedPhone) return;
    setSending(true);
    try {
      // Encontrar cliente_id vinculado
      const conv = conversations.find(c => c.phone === selectedPhone);
      await api.post('/whatsapp/send-text', {
        to: selectedPhone,
        text: newMessage,
        cliente_id: conv?.cliente_id || null,
      });
      setNewMessage('');
      // Recarregar mensagens
      setTimeout(() => loadMessages(selectedPhone), 500);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Erro ao enviar mensagem';
      toast.error(detail);
    } finally {
      setSending(false);
    }
  };

  const handleStartNewChat = () => {
    if (!newChatPhone.trim()) return;
    const clean = newChatPhone.replace(/\D/g, '');
    setSelectedPhone(clean);
    setShowNewChat(false);
    setNewChatPhone('');
    setMessages([]);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'sent': return <Check className="w-3.5 h-3.5 text-gray-400" />;
      case 'delivered': return <CheckCheck className="w-3.5 h-3.5 text-gray-400" />;
      case 'read': return <CheckCheck className="w-3.5 h-3.5 text-blue-500" />;
      case 'failed': return <AlertCircle className="w-3.5 h-3.5 text-red-500" />;
      default: return <Clock className="w-3.5 h-3.5 text-gray-300" />;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } catch (err) {
      console.error('Erro formatTime:', err);
      return '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const d = new Date(dateStr);
      return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    } catch (err) {
      console.error('Erro formatDate:', err);
      return '';
    }
  };

  const formatPhone = (phone) => {
    if (!phone) return '';
    if (phone.length === 13 && phone.startsWith('55')) {
      return `+${phone.slice(0,2)} (${phone.slice(2,4)}) ${phone.slice(4,9)}-${phone.slice(9)}`;
    }
    return `+${phone}`;
  };

  const filteredConvs = conversations.filter(c => {
    const term = searchTerm.toLowerCase();
    return (
      (c.phone || '').includes(term) ||
      (c.cliente_nome || '').toLowerCase().includes(term) ||
      (c.last_message?.from_name || '').toLowerCase().includes(term) ||
      (c.last_message?.content || '').toLowerCase().includes(term)
    );
  });

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="whatsapp-chat-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-green-100 rounded-lg">
          <MessageCircle className="w-6 h-6 text-green-600" />
        </div>
        <div>
          <TitleWithHelp
            title="WhatsApp"
            description="Gerencie conversas bidirecionais com seus clientes via WhatsApp. As mensagens são automaticamente vinculadas aos contatos do CRM."
            connections={['CRM', 'Clientes', 'Oportunidades']}
          >
            <h1 className="text-2xl font-bold text-gray-900" data-testid="whatsapp-chat-title">WhatsApp</h1>
          </TitleWithHelp>
          <p className="text-sm text-gray-500">Conversas e mensagens</p>
        </div>
      </div>

      <div className="flex-1 flex gap-4 min-h-0">
        {/* Sidebar - Lista de conversas */}
        <div className="w-80 flex flex-col border border-gray-200 rounded-lg bg-white" data-testid="conversations-sidebar">
          {/* Busca */}
          <div className="p-3 border-b border-gray-100 space-y-2">
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar conversa..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-8 h-9 text-sm"
                data-testid="search-conversations"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-full text-xs"
              onClick={() => setShowNewChat(!showNewChat)}
              data-testid="new-chat-btn"
            >
              <Phone className="w-3.5 h-3.5 mr-1.5" /> Nova conversa
            </Button>
            {showNewChat && (
              <div className="flex gap-1.5">
                <Input
                  placeholder="Telefone com DDI (5511...)"
                  value={newChatPhone}
                  onChange={e => setNewChatPhone(e.target.value)}
                  className="h-8 text-xs"
                  data-testid="new-chat-phone-input"
                  onKeyDown={e => e.key === 'Enter' && handleStartNewChat()}
                />
                <Button size="sm" className="h-8 px-2" onClick={handleStartNewChat} data-testid="start-new-chat-btn">
                  <Send className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
          </div>

          {/* Lista */}
          <div className="flex-1 overflow-y-auto">
            {loadingConvs ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            ) : filteredConvs.length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-400">
                Nenhuma conversa
              </div>
            ) : (
              filteredConvs.map(conv => (
                <button
                  key={conv.phone}
                  className={`w-full text-left px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors ${
                    selectedPhone === conv.phone ? 'bg-green-50 border-l-2 border-l-green-500' : ''
                  }`}
                  onClick={() => setSelectedPhone(conv.phone)}
                  data-testid={`conversation-${conv.phone}`}
                >
                  <div className="flex items-start gap-2.5">
                    <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="w-4 h-4 text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {conv.cliente_nome || conv.last_message?.from_name || formatPhone(conv.phone)}
                        </p>
                        <span className="text-[10px] text-gray-400 flex-shrink-0 ml-2">
                          {formatDate(conv.last_message?.created_at)}
                        </span>
                      </div>
                      {conv.cliente_empresa && (
                        <p className="text-[11px] text-gray-400 truncate">{conv.cliente_empresa}</p>
                      )}
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {conv.last_message?.direction === 'outbound' && 'Você: '}
                        {conv.last_message?.content || ''}
                      </p>
                    </div>
                    {conv.unread_count > 0 && (
                      <span className="bg-green-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center flex-shrink-0">
                        {conv.unread_count}
                      </span>
                    )}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat Area */}
        <div className="flex-1 flex flex-col border border-gray-200 rounded-lg bg-white" data-testid="chat-area">
          {!selectedPhone ? (
            <div className="flex-1 flex items-center justify-center text-gray-400">
              <div className="text-center">
                <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Selecione uma conversa ou inicie uma nova</p>
              </div>
            </div>
          ) : (
            <>
              {/* Chat Header */}
              <div className="px-4 py-3 border-b border-gray-100 flex items-center gap-3" data-testid="chat-header">
                <div className="w-9 h-9 rounded-full bg-green-100 flex items-center justify-center">
                  <User className="w-4 h-4 text-green-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900">{formatPhone(selectedPhone)}</p>
                  {conversations.find(c => c.phone === selectedPhone)?.cliente_nome && (
                    <p className="text-xs text-gray-500">
                      {conversations.find(c => c.phone === selectedPhone)?.cliente_nome}
                    </p>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => loadMessages(selectedPhone)}
                  data-testid="refresh-messages-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>

              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-[#f0f2f5]" data-testid="messages-container">
                {loadingMsgs ? (
                  <div className="flex items-center justify-center h-32">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : messages.length === 0 ? (
                  <div className="text-center py-12 text-sm text-gray-400">
                    Nenhuma mensagem nesta conversa
                  </div>
                ) : (
                  messages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`flex ${msg.direction === 'outbound' ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${msg.id || idx}`}
                    >
                      <div
                        className={`max-w-[70%] rounded-lg px-3 py-2 ${
                          msg.direction === 'outbound'
                            ? 'bg-[#d9fdd3] text-gray-900'
                            : 'bg-white text-gray-900 shadow-sm'
                        }`}
                      >
                        {msg.direction === 'inbound' && msg.from_name && (
                          <p className="text-xs font-medium text-green-700 mb-0.5">{msg.from_name}</p>
                        )}
                        <p className="text-sm whitespace-pre-wrap break-words">{msg.content}</p>
                        <div className="flex items-center justify-end gap-1 mt-1">
                          <span className="text-[10px] text-gray-500">{formatTime(msg.created_at)}</span>
                          {msg.direction === 'outbound' && getStatusIcon(msg.status)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Input */}
              <div className="p-3 border-t border-gray-100 bg-white" data-testid="message-input-area">
                <div className="flex items-center gap-2">
                  <Input
                    placeholder="Digite uma mensagem..."
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1"
                    disabled={sending}
                    data-testid="message-input"
                  />
                  <Button
                    onClick={handleSend}
                    disabled={sending || !newMessage.trim()}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="send-message-btn"
                  >
                    {sending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Send className="w-4 h-4" />
                    )}
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
